import { EXERCISE_TYPE_NAMES, ExerciseList, type Exercise } from '@squats/shared';
import { useEffect, useState } from 'react';
import { Link } from 'wouter';
import { api } from '../api.ts';
import { useUser } from '../auth.tsx';
import { isPlanner } from '../calendar.tsx';
import { ExerciseHistory } from '../components/ExerciseHistory.tsx';
import { Figure3D } from '../components/Figure3D.tsx';
import { MuscleMap } from '../components/MuscleMap.tsx';
import { Sheet } from '../components/Sheet.tsx';
import { loadExerciseContent, type ExerciseContent } from '../figures/content.ts';
import { useApi } from '../useApi.ts';
import { ExerciseForm } from './ExercisesPage.tsx';

export function ExerciseDetailPage({ id }: { id: string }) {
  const user = useUser();
  const { data, error, reload } = useApi(() => api('/api/exercises', ExerciseList), []);
  const [content, setContent] = useState<ExerciseContent | null>(null);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    loadExerciseContent().then(setContent).catch(() => {});
  }, []);

  if (error && !data) return <p className="error">{error}</p>;
  if (!data) return <p className="muted">Loading…</p>;
  const exercise = data.exercises.find((e) => e.id === id);
  if (!exercise) {
    return (
      <div className="stack">
        <Link href="/exercises" className="small">
          ‹ Exercises
        </Link>
        <p>That exercise isn’t in the library (it may have been removed).</p>
      </div>
    );
  }

  const figure = content && (content.figures[exercise.id] ?? (exercise.looksLike ? content.figures[exercise.looksLike] : undefined));
  const guide = content?.guides[exercise.id];
  const looksLike = exercise.looksLike ? data.exercises.find((e) => e.id === exercise.looksLike) : undefined;
  const canEdit = !exercise.builtIn && isPlanner(user);

  return (
    <div className="stack-lg exercise-detail">
      <div className="stack-sm">
        <Link href="/exercises" className="small">
          ‹ Exercises
        </Link>
        <div className="row">
          <h1 className="grow">{exercise.name}</h1>
          {canEdit && (
            <button type="button" className="btn small" onClick={() => setEditing(true)}>
              Edit
            </button>
          )}
        </div>
        <p className="muted">
          {EXERCISE_TYPE_NAMES[exercise.type]}
          {!exercise.builtIn && ' · added by your group'}
        </p>
      </div>

      <div className="detail-grid">
        <section className="card figure-card" aria-label="How it moves">
          {figure && content ? (
            <Figure3D figure={figure} lib={content.lib} primary={exercise.primaryMuscles} secondary={exercise.secondaryMuscles} label={exercise.name} />
          ) : (
            <p className="muted small figure-missing">
              {content ? 'No animation for this one yet. Pick a similar built-in under “Moves like” to borrow its animation.' : 'Loading…'}
            </p>
          )}
          {looksLike && <p className="muted small">Animation borrowed from {looksLike.name}.</p>}
        </section>

        <section className="card stack" aria-labelledby="muscles-h">
          <h2 id="muscles-h">Muscles worked</h2>
          <MuscleMap primary={exercise.primaryMuscles} secondary={exercise.secondaryMuscles} />
        </section>
      </div>

      <ExerciseHistory user={user} exercise={exercise} />

      {guide ? <GuideSections guide={guide} /> : <CustomNotes exercise={exercise} canEdit={canEdit} onEdit={() => setEditing(true)} loaded={content !== null} />}

      <p className="muted small">
        These are general tips, not medical advice. Stop if anything hurts sharply, and check with a professional if you’re working around an injury.
      </p>

      <Sheet open={editing} onClose={() => setEditing(false)} title="Edit exercise">
        {editing && (
          <ExerciseForm
            initial={exercise}
            builtIns={data.exercises.filter((e) => e.builtIn)}
            onDone={() => {
              setEditing(false);
              reload();
            }}
          />
        )}
      </Sheet>
    </div>
  );
}

function GuideSections({ guide }: { guide: NonNullable<ExerciseContent['guides'][string]> }) {
  return (
    <>
      <p className="lead">{guide.summary}</p>
      <section className="card stack" aria-labelledby="setup-h">
        <h2 id="setup-h">Set up</h2>
        <ul className="guide-list">
          {guide.setup.map((t) => (
            <li key={t}>{t}</li>
          ))}
        </ul>
      </section>
      <section className="card stack" aria-labelledby="steps-h">
        <h2 id="steps-h">How to do it</h2>
        <ol className="guide-list steps">
          {guide.steps.map((t) => (
            <li key={t}>{t}</li>
          ))}
        </ol>
      </section>
      <section className="card stack" aria-labelledby="cues-h">
        <h2 id="cues-h">Form tips</h2>
        <ul className="guide-list">
          {guide.cues.map((t) => (
            <li key={t}>{t}</li>
          ))}
        </ul>
      </section>
      <section className="card stack safety" aria-labelledby="safety-h">
        <h2 id="safety-h">Stay safe</h2>
        <ul className="guide-list">
          {guide.safety.map((t) => (
            <li key={t}>{t}</li>
          ))}
        </ul>
      </section>
    </>
  );
}

function CustomNotes({ exercise, canEdit, onEdit, loaded }: { exercise: Exercise; canEdit: boolean; onEdit: () => void; loaded: boolean }) {
  if (!loaded) return null;
  return (
    <section className="card stack" aria-labelledby="notes-h">
      <h2 id="notes-h">How to do it</h2>
      {exercise.notes ? (
        <p className="notes">{exercise.notes}</p>
      ) : (
        <p className="muted">
          No notes yet.{' '}
          {canEdit && (
            <button type="button" className="linkish" onClick={onEdit}>
              Add setup, steps and safety tips
            </button>
          )}
        </p>
      )}
    </section>
  );
}
